import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Download, ListOrdered, Network, Flag, Play, X,
} from 'lucide-react';
import { projectsApi, driversApi, workItemsApi, exportApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import type {
  Project, ProjectStatus, RAGStatus, AnnualStrategicDriver, WorkItem, ProjectOutcome,
} from '../types/models';
import HelpPanel from '../components/HelpPanel';
import DriverPill from '../components/DriverPill';
import {
  selectNextMilestone,
  selectNextAction,
  rollupRag,
  describeMilestone,
} from '../utils/projectPortfolio';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { apiErrorMessage, isPlanLimitRefusal } from '../api/errors';
import { useEntitlements } from '../context/EntitlementsContext';
import { usePermissions } from '../context/PermissionsContext';

// ---------------------------------------------------------------------------
// ProjectsPage — portfolio overview. Header + portfolio RAG rollup +
// search/filter bar + grid of project cards (grouped by strategic driver by
// default; toggle for flat list). New Project dialog at the end.
// ---------------------------------------------------------------------------

const projectStatuses: ProjectStatus[] = ['Planning', 'Active', 'OnHold', 'Completed', 'Cancelled'];

const NONE = '__none__';

const RAG_BAR: Record<RAGStatus, string> = {
  Green: 'border-l-emerald-500',
  Amber: 'border-l-amber-500',
  Red: 'border-l-rose-500',
};

const RAG_CHIP: Record<RAGStatus, string> = {
  Green: 'border-emerald-500/60 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  Amber: 'border-amber-500/60 bg-amber-500/15 text-amber-700 dark:text-amber-400',
  Red: 'border-rose-500/60 bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

const RAG_BAR_BG: Record<RAGStatus, string> = {
  Green: 'bg-emerald-500',
  Amber: 'bg-amber-500',
  Red: 'bg-rose-500',
};

const STATUS_CHIP: Record<ProjectStatus, string> = {
  Planning: 'border-blue-500/60 text-blue-700 dark:text-blue-400',
  Active: 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400',
  OnHold: 'border-amber-500/60 text-amber-700 dark:text-amber-400',
  Completed: 'border-border text-muted-foreground',
  Cancelled: 'border-rose-500/60 text-rose-700 dark:text-rose-400',
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth(); // eslint-disable-line @typescript-eslint/no-unused-vars
  const { toast } = useToast();
  // Projects can come without strategic drivers: then there is no driver to
  // load, filter, group by or pick (existing links are kept).
  const driversIncluded = useEntitlements().has('strategicDrivers');
  // An admin can take project writes or exports away per user.
  const { can } = usePermissions();
  const canManage = can('canManageProjects');
  const canExport = can('canExportData');
  const [projects, setProjects] = useState<Project[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [allWorkItems, setAllWorkItems] = useState<WorkItem[]>([]);
  const [allOutcomes, setAllOutcomes] = useState<Record<string, ProjectOutcome[]>>({});
  const [, setLoading] = useState(true);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDriver, setFilterDriver] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupByDriver, setGroupByDriver] = useState<'grouped' | 'flat'>('grouped');

  // New project dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState<Partial<Project>>({
    title: '',
    description: '',
    status: 'Planning',
    strategicDriverId: '',
    startDate: '',
    targetEndDate: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsData, driversData, workItemsData] = await Promise.all([
        projectsApi.getAll(),
        driversIncluded
          ? driversApi.getActive().catch(() => [] as AnnualStrategicDriver[])
          : Promise.resolve([] as AnnualStrategicDriver[]),
        workItemsApi.getAll(),
      ]);
      setProjects(projectsData);
      setDrivers(driversData);
      setAllWorkItems(workItemsData);

      // Load outcomes for each project
      const outcomesByProject: Record<string, ProjectOutcome[]> = {};
      await Promise.all(
        projectsData.map(async (p) => {
          try {
            outcomesByProject[p.id] = await projectsApi.getOutcomes(p.id);
          } catch {
            outcomesByProject[p.id] = [];
          }
        })
      );
      setAllOutcomes(outcomesByProject);
    } catch (err) {
      // silent-by-design: page load.
      console.error('Failed to load projects data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [driversIncluded]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProjects = useMemo(() => {
    let result = projects.filter(p => !p.isArchived);
    if (filterStatus) result = result.filter(p => p.status === filterStatus);
    if (filterDriver) result = result.filter(p => p.strategicDriverId === filterDriver);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [projects, filterStatus, filterDriver, searchQuery]);

  const groupedProjects = useMemo(() => {
    if (groupByDriver === 'flat' || !driversIncluded) return null;
    const groups: Record<string, { driver: AnnualStrategicDriver | null; projects: Project[] }> = {};
    drivers.forEach(d => {
      groups[d.id] = { driver: d, projects: [] };
    });
    groups['_unlinked'] = { driver: null, projects: [] };

    filteredProjects.forEach(p => {
      const key = p.strategicDriverId && groups[p.strategicDriverId] ? p.strategicDriverId : '_unlinked';
      groups[key].projects.push(p);
    });

    return Object.entries(groups).filter(([, g]) => g.projects.length > 0);
  }, [filteredProjects, drivers, groupByDriver, driversIncluded]);

  const projectWorkItemStats = useMemo(() => {
    const stats: Record<string, { open: number; total: number }> = {};
    allWorkItems.forEach(wi => {
      if (wi.projectId) {
        if (!stats[wi.projectId]) stats[wi.projectId] = { open: 0, total: 0 };
        stats[wi.projectId].total++;
        if (!['Done', 'Cancelled'].includes(wi.status)) stats[wi.projectId].open++;
      }
    });
    return stats;
  }, [allWorkItems]);

  const projectOutcomeStats = useMemo(() => {
    const stats: Record<string, { achieved: number; total: number }> = {};
    Object.entries(allOutcomes).forEach(([projectId, outcomes]) => {
      stats[projectId] = {
        achieved: outcomes.filter(o => o.status === 'Achieved').length,
        total: outcomes.length,
      };
    });
    return stats;
  }, [allOutcomes]);

  const getDriver = (driverId?: string) =>
    driverId ? drivers.find(d => d.id === driverId) ?? null : null;

  const getDaysRemaining = (targetEndDate?: string) => {
    if (!targetEndDate) return null;
    const target = new Date(targetEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const diff = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleCreateProject = async () => {
    if (!newProject.title) return;
    try {
      const payload = {
        ...newProject,
        strategicDriverId: newProject.strategicDriverId || undefined,
        startDate: newProject.startDate || undefined,
        targetEndDate: newProject.targetEndDate || undefined,
      };
      await projectsApi.create(payload);
      setDialogOpen(false);
      setNewProject({ title: '', description: '', status: 'Planning', strategicDriverId: '', startDate: '', targetEndDate: '' });
      loadData();
    } catch (err: any) {
      console.error('Failed to create project', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to create project' });
    }
  };

  // Portfolio health rollup — drives the banner above the filter bar.
  const portfolioRag = useMemo(() => rollupRag(filteredProjects), [filteredProjects]);

  const handleExportCsv = async () => {
    try {
      const response = await exportApi.downloadProjectsCsv();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `projects-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // A plan without projects: the upgrade dialog already says so.
      if (isPlanLimitRefusal(err)) return;
      alert(apiErrorMessage(err, 'Failed to export projects.'));
    }
  };

  const renderProjectCard = (project: Project) => {
    const driver = getDriver(project.strategicDriverId);
    const daysRemaining = getDaysRemaining(project.targetEndDate);
    const wiStats = projectWorkItemStats[project.id] || { open: 0, total: 0 };
    const outcomeStats = projectOutcomeStats[project.id] || { achieved: 0, total: 0 };
    const nextMilestone = selectNextMilestone(allOutcomes[project.id] || []);
    const projectItems = allWorkItems.filter(w => w.projectId === project.id);
    const nextAction = selectNextAction(projectItems);

    return (
      <button
        key={project.id}
        type="button"
        onClick={() => navigate(`/projects/${project.id}`)}
        className={cn(
          'h-full w-full text-left transition-colors',
          'rounded-lg border-l-[3px]',
          RAG_BAR[project.ragStatus],
        )}
      >
        <Card className={cn('h-full transition-colors hover:bg-muted/30')}>
          <CardContent>
            <div className="mb-2 flex items-start justify-between gap-2">
              <p className="text-base font-semibold">{project.title}</p>
              <div className="flex shrink-0 items-center gap-1">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                    RAG_CHIP[project.ragStatus],
                  )}
                >
                  {project.ragStatus}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[10px] font-medium',
                    STATUS_CHIP[project.status],
                  )}
                >
                  {project.status}
                </span>
              </div>
            </div>

            <div className="mb-3">
              <DriverPill driver={driver} />
            </div>

            <div className="mb-3">
              <div className="mb-1 flex justify-between">
                <p className="text-xs text-muted-foreground">Progress</p>
                <p className="text-xs font-semibold tabular-nums">{project.progressPercentage}%</p>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('h-full transition-all', RAG_BAR_BG[project.ragStatus])}
                  style={{ width: `${project.progressPercentage}%` }}
                />
              </div>
            </div>

            {nextMilestone && (
              <div className="mb-1.5 flex items-start gap-1.5">
                <Flag
                  className={cn(
                    'mt-[2px] h-4 w-4 shrink-0',
                    nextMilestone.overdue
                      ? 'text-rose-600 dark:text-rose-400'
                      : nextMilestone.daysUntil !== null && nextMilestone.daysUntil <= 7
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-muted-foreground',
                  )}
                />
                <p
                  className={cn(
                    'text-xs leading-snug',
                    nextMilestone.overdue
                      ? 'font-semibold text-rose-600 dark:text-rose-400'
                      : 'text-muted-foreground',
                  )}
                >
                  {describeMilestone(nextMilestone)}
                </p>
              </div>
            )}
            {nextAction && (
              <div className="mb-2 flex items-start gap-1.5">
                <Play className="mt-[2px] h-4 w-4 shrink-0 text-primary" />
                <p className="text-xs leading-snug text-muted-foreground">
                  Next action: {nextAction.title}
                </p>
              </div>
            )}

            <p className="mb-1 text-xs text-muted-foreground">
              {outcomeStats.achieved}/{outcomeStats.total} outcomes · {wiStats.open} open items
            </p>

            {project.targetEndDate && daysRemaining !== null && (
              <p
                className={cn(
                  'text-xs',
                  daysRemaining < 0
                    ? 'font-semibold text-rose-600 dark:text-rose-400'
                    : daysRemaining <= 7
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-muted-foreground',
                )}
              >
                {daysRemaining < 0
                  ? `${Math.abs(daysRemaining)} days overdue`
                  : daysRemaining === 0
                    ? 'Due today'
                    : `${daysRemaining} days remaining`}
              </p>
            )}
          </CardContent>
        </Card>
      </button>
    );
  };

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-2xl font-light tracking-tight">Projects</h1>
        <div className="flex items-center gap-2">
          {canExport && (
            <Button variant="outline" onClick={handleExportCsv} className="gap-1.5">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          )}
          {canManage && (
            <Button onClick={() => setDialogOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          )}
        </div>
      </div>

      <HelpPanel title="About Projects" storageKey="projects">
        Projects connect your strategic drivers to daily execution. Track outcomes, indicators, and time invested.
      </HelpPanel>

      {/* Portfolio RAG rollup */}
      {portfolioRag.total > 0 && (
        <Card
          className={cn(
            'mb-4 border-l-[3px]',
            portfolioRag.red > 0
              ? 'border-l-rose-500'
              : portfolioRag.amber > 0
                ? 'border-l-amber-500'
                : 'border-l-emerald-500',
          )}
        >
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-semibold">Portfolio health</p>
              <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', RAG_CHIP.Red)}>
                {portfolioRag.red} Red
              </span>
              <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', RAG_CHIP.Amber)}>
                {portfolioRag.amber} Amber
              </span>
              <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', RAG_CHIP.Green)}>
                {portfolioRag.green} Green
              </span>
              <p className="text-xs text-muted-foreground">
                {portfolioRag.healthPercent}% green across {portfolioRag.total} active
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select
          value={filterStatus || NONE}
          onValueChange={(v) => setFilterStatus(v === NONE ? '' : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>All statuses</SelectItem>
            {projectStatuses.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {driversIncluded && (
          <>
            <Select
              value={filterDriver || NONE}
              onValueChange={(v) => setFilterDriver(v === NONE ? '' : v)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Strategic Driver" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>All drivers</SelectItem>
                {drivers.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div
              role="radiogroup"
              aria-label="Layout"
              className="inline-flex rounded-md border border-border bg-card/40 p-0.5"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    role="radio"
                    aria-checked={groupByDriver === 'grouped'}
                    tabIndex={groupByDriver === 'grouped' ? 0 : -1}
                    size="sm"
                    variant={groupByDriver === 'grouped' ? 'default' : 'ghost'}
                    onClick={() => setGroupByDriver('grouped')}
                    className="h-7 px-2"
                  >
                    <Network className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Group by driver</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    role="radio"
                    aria-checked={groupByDriver === 'flat'}
                    tabIndex={groupByDriver === 'flat' ? 0 : -1}
                    size="sm"
                    variant={groupByDriver === 'flat' ? 'default' : 'ghost'}
                    onClick={() => setGroupByDriver('flat')}
                    className="h-7 px-2"
                  >
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Flat list</TooltipContent>
              </Tooltip>
            </div>
          </>
        )}
        {(searchQuery || filterStatus || filterDriver) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setSearchQuery(''); setFilterStatus(''); setFilterDriver(''); }}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {/* Projects */}
      {filteredProjects.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className={cn('text-sm text-muted-foreground', canManage && 'mb-3')}>No projects found</p>
            {canManage && (
              <Button variant="outline" onClick={() => setDialogOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Create your first project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : groupByDriver === 'grouped' && groupedProjects ? (
        groupedProjects.map(([key, group]) => (
          <div key={key} className="mb-6">
            <h2 className="mb-3 text-base font-semibold text-muted-foreground">
              {group.driver ? group.driver.title : 'Other Projects'}
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {group.projects.map(renderProjectCard)}
            </div>
          </div>
        ))
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map(renderProjectCard)}
        </div>
      )}

      {/* New Project dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="proj-title">Title <span className="text-destructive">*</span></Label>
              <Input
                id="proj-title"
                required
                value={newProject.title || ''}
                onChange={(e) => setNewProject(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="proj-desc">Description</Label>
              <Textarea
                id="proj-desc"
                rows={3}
                value={newProject.description || ''}
                onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            {driversIncluded && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="proj-driver">Strategic Driver</Label>
                <Select
                  value={newProject.strategicDriverId || NONE}
                  onValueChange={(v) => setNewProject(prev => ({ ...prev, strategicDriverId: v === NONE ? '' : v }))}
                >
                  <SelectTrigger id="proj-driver"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="proj-status">Status</Label>
              <Select
                value={newProject.status || 'Planning'}
                onValueChange={(v) => setNewProject(prev => ({ ...prev, status: v as ProjectStatus }))}
              >
                <SelectTrigger id="proj-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {projectStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="proj-start">Start Date</Label>
                <Input
                  id="proj-start"
                  type="date"
                  value={newProject.startDate || ''}
                  onChange={(e) => setNewProject(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="proj-end">Target End Date</Label>
                <Input
                  id="proj-end"
                  type="date"
                  value={newProject.targetEndDate || ''}
                  onChange={(e) => setNewProject(prev => ({ ...prev, targetEndDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateProject} disabled={!newProject.title}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
